import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useTheme } from "@/lib/ThemeContext";

const STEPS = [
  "Sign in to MMNTM and open Settings (your avatar → Settings).",
  "Choose “Data & Export” — request an export first if you want a copy of your data.",
  "Scroll to the Danger zone and press “Delete my account”, then confirm with your password (and your authenticator code if you use one).",
  "Your account is deleted straight away and kept for 30 days in case you change your mind — the restore link is in the email we send you. After that everything is erased for good.",
];

// Bundle F-3: public account-deletion page (the web path app stores require). Works logged out.
export default function DeleteAccountPage() {
  const { tokens: t } = useTheme();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | busy | sent | deleted | error
  const [error, setError] = useState("");
  const card = { background: t.bgSecondary, border: `1px solid ${t.borderColor}` };

  const request = async (e) => {
    e.preventDefault(); setState("busy"); setError("");
    try { await api.accountDeleteRequest(email.trim()); setState("sent"); }
    catch (err) { setError(err?.response?.data?.detail || "Something went wrong. Please try again."); setState("error"); }
  };
  const confirm = async () => {
    setState("busy"); setError("");
    try { await api.accountDeleteConfirm(token); setState("deleted"); }
    catch (err) { setError(err?.response?.data?.detail || "This link is invalid, has expired or was already used."); setState("error"); }
  };

  return (
    <div className="min-h-screen px-6 py-12" style={{ background: t.bgPrimary, color: t.textPrimary }} data-testid="delete-account-page">
      <div className="max-w-xl mx-auto space-y-8">
        <header className="space-y-2">
          <Link to="/login" className="text-xs hover:underline" style={{ color: t.textMuted }} data-testid="delete-account-back">← MMNTM</Link>
          <h1 className="text-3xl sm:text-4xl font-bold">Delete your MMNTM account</h1>
          <p className="text-sm" style={{ color: t.textMuted }}>Deleting removes your tasks, notes, focus sessions, projects you own, settings and sign-in methods. Projects shared with you stay with their owners.</p>
        </header>

        {token ? (
          <section className="rounded-2xl p-6 space-y-4" style={card} data-testid="delete-account-confirm">
            <h2 className="text-base md:text-lg font-bold">Confirm the deletion</h2>
            {state === "deleted" ? (
              <p className="text-sm" data-testid="delete-account-done">Done — your account has been deleted. We've emailed you a link that restores it within 30 days, should you change your mind.</p>
            ) : (
              <>
                <p className="text-sm" style={{ color: t.textMuted }}>Pressing the button deletes the account this link was sent for. The link works once.</p>
                {error && <p className="text-sm" style={{ color: "#F87171" }} data-testid="delete-account-error">{error}</p>}
                <button type="button" onClick={confirm} disabled={state === "busy"} className="px-4 py-2.5 text-sm font-semibold rounded-xl disabled:opacity-50" style={{ background: "#DC2626", color: "#fff" }} data-testid="delete-account-confirm-btn">
                  {state === "busy" ? "Deleting…" : "Delete my account"}
                </button>
              </>
            )}
          </section>
        ) : (
          <>
            <section className="space-y-3" data-testid="delete-account-steps">
              <h2 className="text-base md:text-lg font-bold">From inside the app</h2>
              <ol className="space-y-2 text-sm list-decimal pl-5" style={{ color: t.textMuted }}>
                {STEPS.map((s) => <li key={s}>{s}</li>)}
              </ol>
            </section>
            <section className="rounded-2xl p-6 space-y-4" style={card} data-testid="delete-account-form">
              <h2 className="text-base md:text-lg font-bold">Can't sign in?</h2>
              <p className="text-sm" style={{ color: t.textMuted }}>Enter the e-mail address of the account. If it exists, we'll send a confirmation link that is valid for 48 hours.</p>
              {state === "sent" ? (
                <p className="text-sm" data-testid="delete-account-sent">If that address has an account, a confirmation link is on its way. Check your inbox (and spam).</p>
              ) : (
                <form onSubmit={request} className="flex flex-col sm:flex-row gap-2">
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="flex-1 px-3 py-2.5 text-sm rounded-xl outline-none" style={{ background: t.bgPrimary, border: `1px solid ${t.borderColor}`, color: t.textPrimary }} data-testid="delete-account-email" />
                  <button type="submit" disabled={state === "busy"} className="px-4 py-2.5 text-sm font-semibold rounded-xl disabled:opacity-50" style={{ background: t.accent, color: "#fff" }} data-testid="delete-account-submit">
                    {state === "busy" ? "Sending…" : "Email me a link"}
                  </button>
                </form>
              )}
              {error && <p className="text-sm" style={{ color: "#F87171" }} data-testid="delete-account-error">{error}</p>}
            </section>
          </>
        )}
        <footer className="text-xs" style={{ color: t.textMuted }}>
          See our <Link to="/privacy" className="underline">Privacy Policy</Link> for what we keep and for how long.
        </footer>
      </div>
    </div>
  );
}
